import { useAllMarketsStaticData } from 'client/hooks/markets/marketsStaticData/useAllMarketsStaticData';
import { TabIdentifiable } from 'client/hooks/ui/tabs/types';
import { useTabs } from 'client/hooks/ui/tabs/useTabs';
import { useDialog } from 'client/modules/app/dialogs/hooks/useDialog';
import { MarginModeType } from 'client/modules/localstorage/userSettings/types/tradingSettings';
import { useSelectedPerpMarginMode } from 'client/pages/PerpTrading/hooks/useSelectedPerpMarginMode';
import { getDefaultPerpLeverage } from 'client/pages/PerpTrading/utils/defaultPerpLeverage';
import { useCallback, useEffect, useState } from 'react';

const PERP_MARGIN_MODE_TABS: TabIdentifiable<MarginModeType>[] = [
  {
    id: 'isolated',
  },
  {
    id: 'cross',
  },
];

export function usePerpMarginModeDialog({ productId }: { productId: number }) {
  const { hide } = useDialog();
  const {
    isIsolatedOnlyMarket,
    savedMarginMode,
    selectedMarginMode,
    setSelectedMarginMode: setSavedMarginMode,
  } = useSelectedPerpMarginMode(productId);
  const { data: allMarketsStaticData } = useAllMarketsStaticData();
  const currentMarket = allMarketsStaticData?.perpMarkets[productId];

  const maxLeverage = currentMarket?.maxLeverage ?? 1;

  // Local state
  const {
    selectedTabId: selectedMarginModeTabId,
    setSelectedUntypedTabId: setSelectedMarginModeTabId,
    tabs,
  } = useTabs(PERP_MARGIN_MODE_TABS);
  const [leverage, setLeverage] = useState(getDefaultPerpLeverage(maxLeverage));
  const [enableIsoBorrows, setEnableIsoBorrows] = useState(true);

  // Sync with saved data
  useEffect(() => {
    const nextMarginMode = isIsolatedOnlyMarket
      ? selectedMarginMode
      : savedMarginMode;
    setSelectedMarginModeTabId(nextMarginMode.mode);
    setLeverage(nextMarginMode.leverage);

    if (nextMarginMode.mode === 'isolated') {
      setEnableIsoBorrows(nextMarginMode.enableBorrows);
    }
  }, [
    isIsolatedOnlyMarket,
    savedMarginMode,
    selectedMarginMode,
    setSelectedMarginModeTabId,
  ]);

  const onSaveClick = useCallback(() => {
    if (isIsolatedOnlyMarket) {
      setSavedMarginMode({
        mode: 'isolated',
        leverage,
        enableBorrows: enableIsoBorrows,
      });
      hide();
      return;
    }

    switch (selectedMarginModeTabId) {
      case 'isolated':
        setSavedMarginMode({
          mode: 'isolated',
          leverage,
          enableBorrows: enableIsoBorrows,
        });
        break;
      case 'cross':
        setSavedMarginMode({
          mode: 'cross',
          leverage,
        });
        break;
    }

    hide();
  }, [
    selectedMarginModeTabId,
    isIsolatedOnlyMarket,
    hide,
    setSavedMarginMode,
    leverage,
    enableIsoBorrows,
  ]);

  const marginModeTabs = isIsolatedOnlyMarket
    ? tabs.filter((tab) => tab.id === 'isolated')
    : tabs;

  return {
    hide,
    isIsolatedOnlyMarket,
    selectedMarginModeTabId,
    setSelectedMarginModeTabId,
    marginModeTabs,
    leverage,
    setLeverage,
    currentMarket,
    maxLeverage,
    enableIsoBorrows,
    setEnableIsoBorrows,
    onSaveClick,
  };
}
